























































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import * as routes from "@/employer/route-names";
import { FundingOption, Permission, UserRoleInt, UserPermission }  from "@/shared/types";
import Swinger from "@/employer/components/shared/Swinger.vue";
import { isPartnerPage } from "@/employer/navSelector";
import { useUserStore } from "@/shared/store/user";
import { useNavigationStore } from "@/shared/store/navigation";
import { usePortalCustomizationStore } from "@/shared/store/portalCustomization";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useRoleStore } from "@/shared/store/role";

@Component({
  components: {
    Swinger
  }
})
export default class MainNavigation extends Vue {
  userStore = useUserStore();
  navigationStore = useNavigationStore();
  portalCustomizationStore = usePortalCustomizationStore();
  rolePermissionsStore = useRolePermissionsStore();
  featureFlagStore = useFeatureFlagStore();
  custodianSettingsStore = useCustodianSettingsStore();
  roleStore = useRoleStore();

  @Prop()
  partnerHeaderMode: {
    showCustomerServiceHeader: boolean;
    showEmployerNameHeader: boolean;
  };

  @Prop()
  username: string;

  @Prop()
  partnerId: number;

  routes = routes;

  get navigationRouteName (): string {
    return this.$route.name;
  }

  get isPartnerAccountHolderInvestmentRoute (): boolean {
    return this.routes.PartnerAccountHolderInvestmentRoutes.includes(this.navigationRouteName);
  }

  get displayPartnerUserManagementLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.ManagePartnerUserRead);
  }

  get displayPartnerGroupsLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.PartnerEmployerRead);
  }

  get displayPartnerAccountHoldersLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.PartnerFindEmployeeRead);
  }

  get displayPartnerCommunicationsLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.PartnerCommunicationWidgetRead);
  }

  get displayEmployeesLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.EmployeeRead);
  }

  get displayMyProgramLink (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.employerUserRoles && this.roleStore.hasEmployerRoles && this.isApexUser;
  }

  get displayContributionsLink (): boolean {
    if (!this.rolePermissionsStore.userHasPermission(Permission.PayrollRead)) {
      return false;
    }

    return this.userStore.currentEmployer
      && (this.userStore.currentEmployer.fundingOption !== FundingOption.ErCalculateErPush
        || (this.showFundingMethod && this.userStore.currentEmployer.hasFundingOptionHistory));
  }

  get isHsaCustodian (): boolean {
    return this.custodianSettingsStore.isHsaBankCustodian &&
        this.featureFlagStore.flags.bend16612ContributionHandling;
  }
  get displayAccountHolderExpenses (): boolean {
    return this.custodianSettingsStore.isHsaBankCustodian
       && this.featureFlagStore.featureFlagsAvailable
       && this.featureFlagStore.flags.bend23845EnableAccountHolderExpenses;
  }
  get defaultToManageFundingFileTab (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.fundingOption === FundingOption.ErCalculateBendPull;
  }

  get defaultToHistoryTab (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.fundingOption === FundingOption.ErCalculateErPush;
  }

  get isSettingsRoute (): boolean {
    return this.routes.SettingsRoutes.includes(this.navigationRouteName);
  }

  get shouldShowEmployerReportsLink (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.employerUserRoles && this.roleStore.hasEmployerRoles &&
      this.userStore.currentEmployer.employerUserRoles.includes(UserRoleInt.employerCensus);
  }

  get showFundingMethod (): boolean {
    return this.featureFlagStore.flags.showFundingMethod;
  }

  get showResourceForms (): boolean {
    return this.featureFlagStore.flags.bend16362ResourceFormsInEmployer &&
      this.rolePermissionsStore.userHasPartnerPermission(Permission.PartnerManageFormDocuments, this.userStore.partnerId);
  }

  get isEmployeesRoute (): boolean {
    return this.routes.EmployeesRoutes.includes(this.navigationRouteName);
  }

  get isContributionsRoute (): boolean {
    return this.routes.ContributionsRoutes.includes(this.navigationRouteName);
  }

  get usePartnerNavigation (): boolean {
    return isPartnerPage(this.$route.name);
  }

  get useServiceNavigation (): boolean {
    return this.partnerHeaderMode && this.partnerHeaderMode.showCustomerServiceHeader;
  }

  get partnerHeaderShow (): boolean {
    return this.partnerHeaderMode &&
      (this.partnerHeaderMode.showEmployerNameHeader || this.partnerHeaderMode.showCustomerServiceHeader);
  }

  get useEmployerNavigation (): boolean {
    return this.partnerHeaderMode.showEmployerNameHeader
      || !(this.useServiceNavigation || this.usePartnerNavigation);
  }

  get contributionsLinkTo () {
    if (this.defaultToManageFundingFileTab) {
      return { name: routes.ManagePayrollFundingFile, params: { page: 1 } };
    } else if (this.defaultToHistoryTab) {
      return { name: routes.ContributionsHistory };
    } else {
      return { name: routes.Contributions };
    }
  }

  get displayAccountManagement (): boolean {
    return this.custodianSettingsStore.displayBankAccountHolds;
  }

  get shouldShowPartnerReportsLink (): boolean {
    // currently, only available for Partner Admins
    return this.userStore.currentPartner && this.userStore.currentPartner.partnerUserRoles && this.roleStore.hasPartnerRoles &&
      this.userStore.currentPartner.partnerUserRoles.includes(UserRoleInt.partnerAdmin);
  }

  get shouldShowBulkLiquidationLink (): boolean {
    return this.featureFlagStore.flags.bend23706DisplayBulkUploadForm &&
        this.rolePermissionsStore.userHasPartnerPermission(Permission.BulkLiquidationFileUpload, this.userStore.partnerId);
  }

  get hasInvestmentViewPermission (): boolean {
    return this.rolePermissionsStore.doesUserHavePermission(
      UserPermission.PartnerPortalInvestmentView
    );
  }

  get isApexUser (): boolean {
    return this.userStore.isApexUser();
  }

  created (): void {
    this.$router.afterEach(this.closeNav);
    this.roleStore.getEmployerRoles();
    this.roleStore.getPartnerRoles();
  }
  mounted () {
    // based on the partner context and the account holder being viewed
    const args = { username: this.username, partnerId: this.partnerId, force: true };
    this.custodianSettingsStore.getCustodianSettings(args);
  }

  closeNav (): void {
    this.navigationStore.closeMobileMenu();
  }

  routeToPartnerSecurity () {
    return { name: routes.PartnerSecurity, params: { partnerId: this.userStore.partnerId } };
  }
}
